import { createCheckoutSession } from '@commenda/api';
import {
  InternalBillingSource,
  TBillableProduct,
  TBillingContextValue,
  TBillingLineItem,
  TCheckoutSessionMetadata,
} from '@commenda/types/src/billing/types';
import { TSetNotification } from '@commenda/types/src/random';
import { Currency } from '@prisma/client';
import { useRouter } from 'next/router';
import { useCallback, useMemo, useState } from 'react';
import { BillingContext } from '../context/billingContext';

function BillingProvider({
  initialBillingItems,
  currency,
  children,
  metadata,
  redirectionUrl,
  setNotification,
  internalBillingSource,
  getProductDescription,
  subscriptionId,
  onCheckoutOverride,
  isLoggedIn,
  companyId,
  affiliateFirmId,
  isAffiliateFirmUser,
}: {
  initialBillingItems: TBillableProduct[];
  currency: Currency;
  children: React.ReactNode;
  metadata?: TCheckoutSessionMetadata;
  redirectionUrl: string;
  setNotification: TSetNotification;
  internalBillingSource: InternalBillingSource;
  getProductDescription: (billingItem: TBillableProduct) => React.ReactNode;
  subscriptionId?: string;
  onCheckoutOverride?: (lineItems: TBillingLineItem[]) => Promise<void>;
  isLoggedIn: boolean;
  companyId?: number;
  affiliateFirmId?: string;
  isAffiliateFirmUser?: boolean;
}) {
  const router = useRouter();
  const [billingItems, setBillingItems] =
    useState<TBillableProduct[]>(initialBillingItems);
  const [shouldBillToClient, setShouldBillToClient] = useState(false);

  const subTotal: number = billingItems.reduce(
    (sum, item) => sum + item.totalPriceAfterDiscount,
    0
  );

  const onUnitChange = useCallback((productId: string, newQuantity: number) => {
    if (newQuantity === 0) {
      setBillingItems((prev) => prev.filter((item) => item.id !== productId));
      return;
    }
    setBillingItems((prev) =>
      prev.map((billingItem) => {
        if (billingItem.id === productId) {
          const newTotal = billingItem.netPrice.price * newQuantity;
          return {
            ...billingItem,
            quantity: newQuantity,
            totalPriceBeforeDiscount: newTotal,
            totalPriceAfterDiscount: newTotal,
          };
        }
        return billingItem;
      })
    );
  }, []);

  const onCheckout = useCallback(async (): Promise<boolean> => {
    const lineItems: TBillingLineItem[] = billingItems.map((billingItem) => ({
      productId: billingItem.id,
      quantity: billingItem.quantity,
      billingInterval: billingItem.netPrice.recurring?.interval || undefined,
    }));

    if (onCheckoutOverride) {
      try {
        await onCheckoutOverride(lineItems);
        return true;
      } catch (error) {
        setNotification({
          show: true,
          title: 'Error',
          valid: false,
          message:
            'Something went wrong. Please try again, or contact Team Commenda if you need help.',
        });
        return false;
      }
    }

    if (!isLoggedIn) {
      setNotification({
        show: true,
        title: 'Error',
        valid: false,
        message: 'You must be logged in to checkout.',
      });
      return false;
    }

    const res = await createCheckoutSession({
      ...(!shouldBillToClient && affiliateFirmId
        ? { affiliateFirmId }
        : { companyId }),
      lineItems,
      metadata,
      subscriptionId,
      redirectionUrl,
    });

    if (!res) {
      setNotification({
        show: true,
        title: 'Error',
        valid: false,
        message:
          'Something went wrong. Please try again, or contact Team Commenda if you need help.',
      });
      return false;
    }

    if (!res.ok) {
      setNotification({
        show: true,
        title: 'Error',
        valid: false,
        message:
          'Something went wrong. Please try again, or contact Team Commenda if you need help.',
      });
      return false;
    }

    const data = await res.json();
    if (data.url) {
      await router.push(data.url);
      return true;
    }

    setNotification({
      show: true,
      title: 'Error',
      valid: false,
      message:
        'Something went wrong. Please try again, or contact Team Commenda if you need help.',
    });
    return false;
  }, [
    billingItems,
    onCheckoutOverride,
    isLoggedIn,
    shouldBillToClient,
    affiliateFirmId,
    companyId,
    metadata,
    subscriptionId,
    redirectionUrl,
    setNotification,
    router,
  ]);

  const contextValue: TBillingContextValue = useMemo(
    () => ({
      billingItems,
      setBillingItems,
      onUnitChange,
      subTotal,
      currency,
      onCheckout,
      setNotification,
      getProductDescription,
      internalBillingSource,
      shouldBillToClient,
      setShouldBillToClient,
      isAffiliateFirmUser,
    }),
    [
      billingItems,
      setBillingItems,
      onUnitChange,
      subTotal,
      currency,
      onCheckout,
      setNotification,
      getProductDescription,
      internalBillingSource,
      shouldBillToClient,
      setShouldBillToClient,
      isAffiliateFirmUser,
    ]
  );

  return (
    <BillingContext.Provider value={contextValue}>
      {children}
    </BillingContext.Provider>
  );
}

export { BillingProvider };
